import axios from 'axios';
import { readToken, writeToken, clearToken } from './tokenStorage';

// ─────────────────────────────────────────────────────────────────────
// Global Axios interceptor — automatically attaches JWT token to all
// requests AND handles silent rolling-token refresh + smart 401 logic.
//
// Tim's P0 brief Feb 17: BareMC skal føles som Facebook/Instagram —
// brugeren forbliver logget ind. For at overleve iOS Safari ITP
// (Intelligent Tracking Prevention som purger localStorage efter
// ~7 dage uden interaktion) bruger vi multi-tier storage:
//   1. localStorage (primær)
//   2. document.cookie (overlever ITP-purge)
//   3. sessionStorage (overlever fanen)
// Se /app/frontend/src/utils/tokenStorage.js for detaljer.
// ─────────────────────────────────────────────────────────────────────

// On module load: hydrate header fra storage (heal'er localStorage
// hvis token kun fandtes i cookie).
const bootToken = readToken();
if (bootToken) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${bootToken}`;
}

// Cross-tab sync: hvis user logger ind i én tab, skal alle andre tabs
// også få den nye token.
window.addEventListener('storage', (e) => {
  if (e.key === 'token') {
    if (e.newValue) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${e.newValue}`;
      // Spejl til cookie/sessionStorage så de tre tier'er forbliver konsistente.
      writeToken(e.newValue);
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }
});

// Request interceptor — re-attach token før hvert kald (i tilfælde af
// at det blev sat efter modulen blev loadet).
axios.interceptors.request.use((config) => {
  const token = readToken();
  if (token && config.headers && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// 401-handler — kun auto-logout på /auth/me. Andre 401'ere må KUN
// forwarde fejlen til komponenten (Tim's brief: ingen "transient 401
// midt i en background poll → logout"-bugs).
let _isHandling401 = false;
axios.interceptors.response.use(
  (res) => {
    // ── Rolling token refresh ────────────────────────────────────
    // Backend's RollingTokenMiddleware injicerer X-New-Token når
    // brugerens JWT er inde i 60-dages refresh-window. Skift token
    // silent + skriv til alle 3 storage-tiers så ITP-purge ikke kan
    // ramme. Sammen med 365-dages persistent token = "always logged
    // in" oplevelse uanset om brugeren bruger Safari, Chrome eller PWA.
    try {
      const fresh = res?.headers?.['x-new-token'] || res?.headers?.['X-New-Token'];
      if (fresh && typeof fresh === 'string' && fresh.length > 20) {
        const current = readToken();
        if (fresh !== current) {
          writeToken(fresh);
          axios.defaults.headers.common['Authorization'] = `Bearer ${fresh}`;
        }
      }
    } catch { /* never break a working response over refresh */ }
    return res;
  },
  (err) => {
    const status = err?.response?.status;
    const url = err?.config?.url || '';
    const isMe = url.includes('/auth/me');
    if (status === 401 && isMe && !_isHandling401) {
      _isHandling401 = true;
      try { clearToken(); } catch { /* ignore */ }
      if (typeof window !== 'undefined' &&
          !window.location.pathname.startsWith('/login')) {
        window.location.replace('/login');
      }
      setTimeout(() => { _isHandling401 = false; }, 1000);
    }
    return Promise.reject(err);
  },
);

// API base URL for convenience
export const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
